import React, { useState } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Bot, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const AIQueryPanel = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [normalizing, setNormalizing] = useState(false);

  const handleQuery = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    setResponse(null);
    try {
      const res = await api.adminNlpQuery(query);
      setResponse(res.data);
    } catch (err) {
      toast.error('Query failed: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleNormalizeSpecialties = async () => {
    setNormalizing(true);
    try {
      const res = await api.normalizeSpecialties();
      toast.success(`Normalized ${res.data.updated_count} specialties`);
    } catch (err) {
      toast.error('Normalization failed');
    } finally {
      setNormalizing(false);
    }
  };

  const exampleQueries = [
    "What do users say about citation accuracy?",
    "Top complaints from infectious disease specialists",
    "Feedback trends over the last month",
    "What features do users request most?",
    "Compare feedback from trainees vs faculty"
  ];

  return (
    <div className="space-y-6">
      {/* Query Input */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
            <Bot className="h-5 w-5 text-sky-500" />
            AI-Powered Database Query
          </CardTitle>
          <CardDescription className="dark:text-slate-400">
            Ask natural language questions about user feedback, topics, and analytics
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., What do users say about citation accuracy?"
              className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
              onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
              data-testid="nlp-query-input"
            />
            <Button 
              onClick={handleQuery} 
              disabled={loading || !query.trim()}
              className="rounded-full bg-sky-600 hover:bg-sky-700"
              data-testid="nlp-query-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* Example Queries */}
          <div className="flex flex-wrap gap-2">
            {exampleQueries.map((eq, i) => (
              <button
                key={i}
                onClick={() => setQuery(eq)}
                className="text-xs px-3 py-1 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-full text-slate-600 dark:text-slate-300 transition-colors"
              >
                {eq}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Response */}
      {response && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg dark:text-slate-100">Analysis Result</CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  navigator.clipboard.writeText(response.response);
                  toast.success('Copied to clipboard');
                }}
                className="rounded-full"
                title="Copy to clipboard"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                {response.response}
              </div>
            </div>
            {response.data_summary && (
              <div className="mt-4 flex gap-4 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
                <span>Feedback analyzed: {response.data_summary.feedback_count}</span>
                <span>Topics: {response.data_summary.topic_count}</span>
                <span>Top specialties: {response.data_summary.top_specialties?.slice(0, 3).join(', ')}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Utility Actions */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg dark:text-slate-100">Data Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">Normalize Specialties</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Merge variations like "ID" → "Infectious Diseases"
              </p>
            </div>
            <Button
              onClick={handleNormalizeSpecialties}
              disabled={normalizing}
              variant="outline"
              className="rounded-full"
              data-testid="normalize-specialties-btn"
            >
              {normalizing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Run Normalization
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIQueryPanel;
